import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Cloud, HardDrive, Check, Loader2, Trash2, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_META = {
  google_drive: { label: 'Google Drive', color: '#4285F4', bg: 'bg-blue-500/10', text: 'text-blue-500' },
  onedrive: { label: 'OneDrive', color: '#0078D4', bg: 'bg-sky-500/10', text: 'text-sky-500' },
  dropbox: { label: 'Dropbox', color: '#0061FF', bg: 'bg-indigo-500/10', text: 'text-indigo-500' },
  local: { label: 'Local Files', color: '#10B981', bg: 'bg-emerald-500/10', text: 'text-emerald-500' },
};

const StorageDashboard = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storageUsage, setStorageUsage] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [provRes, dashRes] = await Promise.all([
          axios.get(`${API}/storage/providers`).catch(() => ({ data: { providers: [] } })),
          axios.get(`${API}/dashboard/stats`).catch(() => ({ data: {} })),
        ]);
        setProviders(provRes.data.providers || []);
        setStorageUsage(dashRes.data?.storage || null);
      } catch { /* silent */ }
      setLoading(false);
    };
    load();
  }, []);

  const handleConnect = async (providerId) => {
    try {
      let endpoint = '';
      if (providerId === 'google_drive') endpoint = '/drive/connect';
      else if (providerId === 'onedrive') endpoint = '/storage/onedrive/connect';
      else if (providerId === 'dropbox') endpoint = '/storage/dropbox/connect';
      else return;
      const res = await axios.get(`${API}${endpoint}`);
      if (res.data?.authorization_url) window.location.href = res.data.authorization_url;
      else toast.error('Failed to initiate connection');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    }
  };

  const handleDisconnect = async (providerId) => {
    if (!window.confirm(`Disconnect ${PROVIDER_META[providerId]?.label}? Your linked folders won't be accessible.`)) return;
    try {
      if (providerId === 'google_drive') await axios.post(`${API}/drive/disconnect`);
      else if (providerId === 'onedrive') await axios.post(`${API}/storage/onedrive/disconnect`);
      else if (providerId === 'dropbox') await axios.post(`${API}/storage/dropbox/disconnect`);
      toast.success('Disconnected');
      setProviders(prev => prev.map(p => p.id === providerId ? { ...p, connected: false, email: null } : p));
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const connected = providers.filter(p => p.connected && p.id !== 'local');
  const formatMB = (mb) => mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.5 }}>
      <Card className="p-6  border-2" data-testid="storage-dashboard">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-[#002FA7]/10">
            <Cloud className="w-5 h-5 text-[#002FA7]" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--isp-heading)]">Personal Storage</h3>
            <p className="text-xs text-[var(--isp-muted)]">Manage your cloud storage connections</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div>
        ) : (
          <div className="space-y-4">
            {/* Platform Storage Usage */}
            {storageUsage && (
              <div className="p-4 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]" data-testid="platform-storage-usage">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[var(--isp-heading)] flex items-center gap-2">
                    <HardDrive className="w-4 h-4 text-emerald-500" /> iStudyPro Storage
                  </span>
                  <span className="text-xs text-[var(--isp-body)]">
                    {formatMB(storageUsage.used_mb || 0)} / {formatMB(storageUsage.limit_mb || 500)}
                  </span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      (storageUsage.percentage || 0) >= 90 ? 'bg-red-500' : (storageUsage.percentage || 0) >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(storageUsage.percentage || 0, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-[var(--isp-muted)] mt-1.5">
                  {storageUsage.documents || 0} documents, {storageUsage.audio_files || 0} audio files · {formatMB(storageUsage.remaining_mb || 0)} free
                </p>
              </div>
            )}

            {/* Connected Providers */}
            <div>
              <p className="text-xs font-semibold text-[var(--isp-muted)] uppercase tracking-wider mb-2">Connected Providers</p>
              {connected.length === 0 ? (
                <p className="text-sm text-[var(--isp-body)] py-3">No cloud storage connected yet. Connect one below to reduce platform storage usage.</p>
              ) : (
                <div className="space-y-2">
                  {connected.map(p => {
                    const m = PROVIDER_META[p.id] || {};
                    return (
                      <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)]" data-testid={`connected-${p.id}`}>
                        <div className={`w-9 h-9 rounded-lg ${m.bg} flex items-center justify-center`}>
                          <Cloud className={`w-4 h-4 ${m.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[var(--isp-heading)]">{m.label}</p>
                          <p className="text-[10px] text-[var(--isp-muted)] truncate">{p.email || 'Connected'}</p>
                        </div>
                        <span className="text-[10px] text-emerald-400 px-2 py-1 rounded-full bg-emerald-500/10 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Active
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => handleDisconnect(p.id)} className="text-red-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg" data-testid={`disconnect-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Available Providers */}
            <div>
              <p className="text-xs font-semibold text-[var(--isp-muted)] uppercase tracking-wider mb-2">Available Providers</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {providers.filter(p => !p.connected && p.id !== 'local').map(p => {
                  const m = PROVIDER_META[p.id] || {};
                  return (
                    <button
                      key={p.id}
                      onClick={() => p.available ? handleConnect(p.id) : toast.info(`${m.label} requires API key setup by admin.`)}
                      className="flex items-center gap-2 p-3 rounded-xl border border-dashed border-[var(--isp-glass-border)] hover:border-[#002FA7]/30 transition-all text-left"
                      data-testid={`connect-${p.id}`}
                    >
                      <Cloud className={`w-4 h-4 ${m.text}`} />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-[var(--isp-heading)]">{m.label}</p>
                        <p className="text-[10px] text-[var(--isp-muted)]">{p.available ? 'Click to connect' : 'Setup required'}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tip */}
            <p className="text-[10px] text-[var(--isp-muted)] leading-relaxed pt-2 border-t border-[var(--isp-divider)]">
              Connecting personal cloud storage lets iStudyPro access your documents directly without uploading them, saving platform storage and speeding up processing.
            </p>
          </div>
        )}
      </Card>
    </motion.div>
  );
};

export default StorageDashboard;
